/**
 * Audit Logs Service
 * Handles audit log retrieval and compliance reporting
 */

import { api } from './api'

export interface AuditLog {
  id: string
  user_id: string
  action: string
  entity_type: string
  entity_id: string
  timestamp: string
  details: Record<string, unknown>
  contains_phi: boolean
  ip_address: string
  user_agent: string
}

export interface AuditLogListResponse {
  logs: AuditLog[]
  total: number
  skip: number
  limit: number
}

export interface PHIAccessReportResponse {
  total_phi_access: number
  users: Array<{
    user_id: string
    username: string
    phi_access_count: number
    last_phi_access: string
  }>
  entities: Array<{
    entity_type: string
    entity_id: string
    access_count: number
  }>
}

export interface AuditLogFilters {
  user_id?: string
  entity_type?: string
  entity_id?: string
  action?: string
  contains_phi?: boolean
  start_date?: string
  end_date?: string
  skip?: number
  limit?: number
}

class AuditService {
  private baseUrl = '/api/v1/audit'

  async getAuditLogs(filters: AuditLogFilters = {}): Promise<AuditLogListResponse> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.entity_type) params.append('entity_type', filters.entity_type)
    if (filters.entity_id) params.append('entity_id', filters.entity_id)
    if (filters.action) params.append('action', filters.action)
    if (filters.contains_phi !== undefined) params.append('contains_phi', filters.contains_phi.toString())
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.skip !== undefined) params.append('skip', filters.skip.toString())
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString())

    return api.get<AuditLogListResponse>(`${this.baseUrl}/logs?${params.toString()}`)
  }

  async getPHIAccessReport(): Promise<PHIAccessReportResponse> {
    return api.get<PHIAccessReportResponse>(`${this.baseUrl}/phi-access-report`)
  }

  async getAuditLogDetails(logId: string): Promise<AuditLog> {
    return api.get<AuditLog>(`${this.baseUrl}/logs/${logId}`)
  }
}

export const auditService = new AuditService()
